import React, { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Loader2 } from 'lucide-react';

const AuthCallback = () => {
  const hasProcessed = useRef(false);
  const { processOAuthCallback } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processSession = async () => {
      try {
        // Extract access_token and redirect from URL hash
        // Backend sends: /auth/callback#access_token=xxx&redirect=/dashboard
        const hash = location.hash.slice(1); // remove leading '#'
        const params = new URLSearchParams(hash);

        const accessToken = params.get('access_token');
        const redirectTo  = params.get('redirect');

        if (!accessToken) {
          console.error('No access_token found in URL hash');
          navigate('/login', { replace: true });
          return;
        }

        // Store token and load user profile
        const user = await processOAuthCallback(accessToken);

        // Clear the hash from the URL bar
        window.history.replaceState(null, '', window.location.pathname);

        // Navigate to the saved redirect or role default
        if (redirectTo && redirectTo !== '/login' && redirectTo !== '/register') {
          navigate(redirectTo, { replace: true });
        } else if (user.role === 'admin') {
          navigate('/admin', { replace: true });
        } else if (user.role === 'employee') {
          navigate('/employee', { replace: true });
        } else {
          navigate('/dashboard', { replace: true });
        }
      } catch (error) {
        console.error('OAuth callback error:', error);
        navigate('/login', {
          replace: true,
          state: { error: 'Authentication failed. Please try again.' }
        });
      }
    };

    processSession();
  }, [processOAuthCallback, navigate, location]);

  return (
    <div className="min-h-screen bg-[#F5F0E6] flex items-center justify-center">
      <div className="text-center">
        <div className="w-16 h-16 bg-[#FF5A36] rounded-2xl flex items-center justify-center mx-auto mb-6 animate-pulse">
          <span className="text-white font-bold text-3xl">E</span>
        </div>
        <Loader2 className="w-8 h-8 animate-spin text-[#FF5A36] mx-auto mb-4" />
        <h2
          className="text-xl font-semibold text-[#0A1E40] mb-2"
          style={{ fontFamily: 'Outfit, sans-serif' }}
        >
          Completing Sign In...
        </h2>
        <p className="text-[#4A5568]">Please wait while we verify your account.</p>
      </div>
    </div>
  );
};

export default AuthCallback;